/**
 * Turning "the GPU is on" into "this is costing money".
 *
 * A state chip tells you the meter is running; it does not make anyone act. The
 * numbers do, which is why they sit in the top bar of every page rather than on
 * a costs screen nobody opens.
 *
 * Deliberately computed here from a timestamp and a rate, with no billing API
 * involved: Scaleway's consumption endpoint is authoritative but would put a
 * second external dependency in the top bar, and it also reports storage and IP
 * charges that accrue whether or not anyone is using the GPU -- which is exactly
 * the number *not* to show beside somebody's session.
 */

/** "12 min", "1 h 05 min", "2 d 3 h". Short enough for a top bar. */
export function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return ''
  const minutes = Math.floor(seconds / 60)
  if (minutes < 1) return 'under a minute'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} h ${String(minutes % 60).padStart(2, '0')} min`
  return `${Math.floor(hours / 24)} d ${hours % 24} h`
}

/** Euros to two places, or null when there is no rate to apply. */
export function sessionCost(seconds: number, hourlyRate: number | null): number | null {
  if (hourlyRate === null || !Number.isFinite(seconds) || seconds < 0) return null
  return (seconds / 3600) * hourlyRate
}

/** Seconds the GPU has been up, or null when it is not running. */
export function uptimeSeconds(
  runningSince: string | null,
  now: number = Date.now(),
): number | null {
  if (!runningSince) return null
  const started = new Date(runningSince).getTime()
  if (!Number.isFinite(started)) return null
  // Clamp: a clock skew between this browser and Scaleway must not produce a
  // negative uptime, and reading "-3 min" would undermine every other number.
  return Math.max(0, (now - started) / 1000)
}
